import { IPage } from "@/api/api";
import { getAllInvoices } from "@/api/store";
import { DataTable } from "@/components/dashboard";
import {
    InvoiceStatus,
    InvoiceStatusChip,
} from "@/components/dashboard/orders/status/InvoiceStatusChip";
import { useFilterModel } from "@/hooks";
import { Box, CircularProgress } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { InvoiceDetailModal } from "./InvoiceDetailModal";
import { IInvoice } from "@/api/invoice";

interface InvoiceListProps {
    currentStoreId: number;
}

interface IInvoiceRow {
    id: string;
    number: number;
    amount_due: number;
    amount_remaining: number;
    status: InvoiceStatus;
}

export const InvoiceTable = ({ currentStoreId }: InvoiceListProps) => {
    const [open, setOpen] = useState(false);
    const [invoiceRows, setInvoiceRows] = useState<IInvoiceRow[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<IInvoice | null>(
        null
    );

    const { filterModel, handleFilterModelChange } = useFilterModel();

    // set default pagination model
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const autoSizeOptions = {
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const invoiceTableQueryKey = useMemo(
        () => ["store-invoices", currentStoreId, paginationModel, filterModel],
        [currentStoreId, paginationModel, filterModel]
    );

    const { isPending: invoiceIsPending, data: invoices } = useQuery<
        IPage<IInvoice>
    >({
        queryKey: invoiceTableQueryKey,
        queryFn: () => {
            if (currentStoreId) {
                return getAllInvoices(
                    currentStoreId,
                    paginationModel.page + 1,
                    paginationModel.pageSize,
                    filterModel
                );
            } else throw Error("No invoice item");
        },
        enabled: !!currentStoreId,
    });

    const handleRowClick = (params: GridRowParams) => {
        setOpen(!open);
        setSelectedInvoice(params.row);
    };

    const columns: GridColDef<IInvoiceRow>[] = useMemo(
        () => [
            {
                field: "number",
                headerName: "Invoice ID",
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value: never) => {
                    if (!value) return value;

                    return `${value}`;
                },
                display: "flex",
                sortable: false,
                filterable: false,
            },
            {
                field: "amount_due",
                headerName: "Due",
                display: "flex",
                sortable: false,
                filterable: false,
                renderCell: ({ row }) => {
                    return `$${(row.amount_remaining / 100).toFixed(2)}`;
                },
            },
            {
                field: "amount_remaining",
                headerName: "Remaining",
                display: "flex",
                sortable: false,
                filterable: false,
                renderCell: ({ row }) => {
                    return `$${(row.amount_remaining / 100).toFixed(2)}`;
                },
            },
            {
                field: "status",
                type: "singleSelect",
                display: "flex",
                headerName: "Order Status",
                sortable: false,
                valueOptions: [
                    { value: "draft", label: "Draft" },
                    { value: "open", label: "Open" },
                    { value: "Paid", label: "Paid" },
                    { value: "Uncollectible", label: "Uncollectible" },
                    { value: "Void", label: "Void" },
                ],
                renderCell({ row }) {
                    return (
                        //TODO: UPDATE
                        <InvoiceStatusChip
                            invoiceStatus={row.status}
                            invoice_id={row.id}
                        />
                    );
                },
            },
        ],
        []
    );

    useEffect(() => {
        if (invoices) {
            setInvoiceRows(
                invoices.items?.map((invoice, idx) => {
                    return {
                        id: invoice.id,
                        number: invoices.items.length - idx,
                        amount_due: invoice.amount_due,
                        amount_remaining: invoice.amount_remaining,
                        status: invoice.status,
                    } as IInvoiceRow;
                })
            );
        }
    }, [invoices, filterModel]);

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(invoices?.total || 0);

    const rowCount = useMemo(() => {
        if (invoices?.total !== undefined) {
            rowCountRef.current = invoices.total;
        }
        return rowCountRef.current;
    }, [invoices?.total]);

    if (invoiceIsPending) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ borderRadius: "10px", padding: "1em" }}>
            <DataTable
                loading={!invoices}
                rows={invoiceRows}
                columns={columns}
                onRowClick={handleRowClick}
                sx={{
                    ".MuiDataGrid-cell:focus": {
                        outline: "none",
                    },
                    "& .MuiDataGrid-row:hover": {
                        cursor: "pointer",
                    },
                }}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "id", sort: "desc" }],
                    },
                }}
                autoSize
                autosizeOptions={autoSizeOptions}
                paginationMode={"server"}
                rowCount={rowCount}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                filterMode={"server"}
                sortingMode={"server"}
                onFilterModelChange={handleFilterModelChange}
                onSortModelChange={handleFilterModelChange}
            />
            <InvoiceDetailModal
                open={open}
                currentStoreId={currentStoreId}
                setOpen={setOpen}
                invoice={selectedInvoice}
                setSelectedInvoice={setSelectedInvoice}
                invoiceTableQueryKey={invoiceTableQueryKey}
            />
        </Box>
    );
};
